import { z } from 'zod'
import { Action } from '@manychat/icons'

import { CustomAudienceIGActionType } from 'apps/ads/constants/CustomAudienceType'
import { ActionTypes } from 'common/actions'
import { ConversionType } from 'common/actions/components/ActionItem/SendCapiEventAction/constants'
import { NotifyAdminsSendChannels } from 'common/actions/models/Action/constants'
import { ConversionsApiChannelSchema } from 'shared/api/requests/settings/schemas/ConversionsApiChannelSchema'

import { EmptyArraySchema } from '../base'

/* ----------------------
  Basic / shared schemas
  ---------------------- */

const ActionBaseSchema = z.object({
  id: z.string().optional(),
  _oid: z.string().optional(),
})

const ActionFieldValueSchema = z.object({
  type: z.string(),
  field_name: z.union([z.string(), z.number()]).optional(),
  field_id: z.number().optional(),
  value: z.union([z.string(), z.number()]).optional(),
  valueType: z.string().optional(),
})

const ActionFieldSchema = z.object({
  title: z.string().optional(),
  field_name: z.string(),
  displayType: z.enum(['input', 'select']).optional(),
  value: ActionFieldValueSchema.optional(),
  default: z.union([z.string(), z.number()]).optional(),
  isValid: z.boolean().optional(),
})

const ActionDataMappingValueSchema = z.object({
  type: z.string(),
  field_id: z.number().optional(),
})

const ActionDataMappingSchema = z.object({
  field_name: z.string(),
  title: z.string().optional(),
  value: ActionDataMappingValueSchema.optional(),
})

const ApplicationDataSchema = z.object({
  fields: z.array(ActionFieldSchema),
  mapping: z.array(ActionDataMappingSchema),
})

const NotifyAdminSentToSchema = z.object({
  send_by: z.array(z.nativeEnum(NotifyAdminsSendChannels)),
  user_id: z.number(),
})

/* ----------------------
  Tag actions
  ---------------------- */

const AddTagActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.ADD_TAG),
  tag_id: z.number().optional(),
})

const RemoveTagActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.REMOVE_TAG),
  tag_id: z.number().optional(),
})

/* ----------------------
  Custom audience actions
  ---------------------- */

const CustomAudienceUserActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.CUSTOM_AUDIENCE_USER),
  user_id: z.string().optional(),
  ad_account_id: z.string().optional(),
  custom_audience_id: z.string().optional(),
  action: z.enum(['add', 'remove']).optional(),
})

const CustomAudienceIGUserActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.CUSTOM_AUDIENCE_IG_USER),
  user_id: z.number().nullable().optional(),
  ad_account_id: z.string(),
  custom_audience_id: z.string().optional(),
  custom_audience_name: z.string().optional(),
  action: z.nativeEnum(CustomAudienceIGActionType).optional(),
})

/* ----------------------
  Field operations
  ---------------------- */

const SetCUFActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.SET_CUF),
  field_id: z.number().optional(),
  value: z.string(),
  operation: z.string(),
})

const SetGAFActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.SET_GAF),
  field_id: z.number().optional(),
  value: z.string(),
  operation: z.string(),
})

const UnsetCUFActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.UNSET_CUF),
  field_id: z.number().optional(),
  value: z.string().optional(),
  operation: z.string().optional(),
})

/* ----------------------
  Conversation assignment
  ---------------------- */

const AssignConversationActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.ASSIGN_CONVERSATION),
  user_id: z.number().nullish(),
  group_id: z.number().nullish(),
})

/* ----------------------
  Events / triggers
  ---------------------- */

const FireCustomEventActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.FIRE_CUSTOM_EVENT),
  event_id: z.number().optional(),
  cost: z.union([z.number(), z.string()]).nullish(),
})

const TriggerZapActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.TRIGGER_ZAP),
  event_id: z.string().optional(),
})

/* ----------------------
  Integrations / external
  ---------------------- */

const HubSpotActionDataSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.HUBSPOT_INTEGRATION),
  action: z.string(),
})

const ActiveCampaignActionDataSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.ACTIVE_CAMPAIGN_INTEGRATION),
  action: z.string(),
})

const MailchimpActionDataSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.MAILCHIMP_INTEGRATION),
  action: z.string(),
})

const ConvertKitActionDataSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.CONVERTKIT_INTEGRATION),
  action: z.string(),
})

const ChatGPTActionDataSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.CHATGPT_INTEGRATION),
  action: z.string(),
})

const ClaudeActionDataSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.CLAUDE_INTEGRATION),
  action: z.string(),
})

const DeepSeekActionDataSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.DEEPSEEK_INTEGRATION),
  action: z.string(),
})

const GoogleSheetsActionDataSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.GOOGLE_SHEETS_INTEGRATION),
  action: z.string(),
})

const KlaviyoActionDataSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.KLAVIYO),
  action: z.string(),
})

export type IntegrationAction =
  | z.infer<typeof HubSpotActionDataSchema>
  | z.infer<typeof ActiveCampaignActionDataSchema>
  | z.infer<typeof MailchimpActionDataSchema>
  | z.infer<typeof ConvertKitActionDataSchema>
  | z.infer<typeof ChatGPTActionDataSchema>
  | z.infer<typeof ClaudeActionDataSchema>
  | z.infer<typeof DeepSeekActionDataSchema>
  | z.infer<typeof GoogleSheetsActionDataSchema>
  | z.infer<typeof KlaviyoActionDataSchema>

const ExternalRequestActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.EXTERNAL_REQUEST),
  url: z.string(),
})

const AppsRequestActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.APPS_REQUEST),
  action: z.string(),
  app_id: z.number(),
  data: ApplicationDataSchema.optional(),
  integration: z.string().optional(),
})

/* ----------------------
  Notifications
  ---------------------- */

const NotifyAdminsActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.NOTIFY_ADMINS),
  text: z.string(),
  all_send_by: z.array(z.nativeEnum(NotifyAdminsSendChannels)),
  send_to: z.array(NotifyAdminSentToSchema),
  options: z
    .object({
      send_link_to_live_chat: z.boolean().optional(),
    })
    .or(EmptyArraySchema)
    .optional(),
})

/* ----------------------
  Flow control
  ---------------------- */

const StartFlowActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.START_FLOW),
  flow_ns: z.string().optional(),
})

const PauseAutomationsActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.PAUSE_AUTOMATIONS),
  pause_duration: z.number().optional(),
})

/* ----------------------
  Sequence subscribe/unsubscribe
  ---------------------- */

const SubscribeSequenceActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.SUBSCRIBE),
  sequence_id: z.union([z.number(), z.string()]).optional(),
})

const UnsubscribeSequenceActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.UNSUBSCRIBE),
  sequence_id: z.union([z.number(), z.string()]).optional(),
})

/* ----------------------
  Menus / other
  ---------------------- */

const SetMainMenuActionSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.SET_MAIN_MENU),
  main_menu_flow_ns: z.string().optional(),
})

const MetaConversionsApiSchema = ActionBaseSchema.extend({
  type: z.literal(ActionTypes.SEND_EVENT_TO_CAPI),
  conversion_type: z.enum(ConversionType),
  channel: ConversionsApiChannelSchema,
  custom_data: z.any(),
})

/* ----------------------
  Empty (no extra payload) actions
  ---------------------- */

const EmptyActionTypes = [
  ActionTypes.CLOSE_CONVERSATION,
  ActionTypes.DELETE_SUBSCRIBER,
  ActionTypes.GLOBAL_UNSUBSCRIBE,
  ActionTypes.OPEN_CONVERSATION,
  ActionTypes.SET_EMAIL_OPTOUT,
  ActionTypes.SET_EMAIL_OPTIN,
  ActionTypes.SET_FB_OPTOUT,
  ActionTypes.SET_FB_OPTIN,
  ActionTypes.SET_IG_OPTIN,
  ActionTypes.SET_IG_OPTOUT,
  ActionTypes.SET_SMS_OPTOUT,
  ActionTypes.SET_SMS_OPTIN,
  ActionTypes.SET_WA_OPTOUT,
  ActionTypes.TRIGGER_INTEGROMAT,
] as const

const EmptyActionSchemas = EmptyActionTypes.map((type) =>
  z.object({ id: z.string().optional(), type: z.literal(type), _oid: z.string().optional() }),
)

export type EmptyAction = z.infer<typeof EmptyActionSchemas[number]>

/* ----------------------
  Exported union + types
  ---------------------- */

export const ActionSchema = z.discriminatedUnion('type', [
  AddTagActionSchema,
  RemoveTagActionSchema,
  AssignConversationActionSchema,
  AppsRequestActionSchema,
  ExternalRequestActionSchema,
  FireCustomEventActionSchema,
  NotifyAdminsActionSchema,
  PauseAutomationsActionSchema,
  SetCUFActionSchema,
  SetGAFActionSchema,
  SetMainMenuActionSchema,
  MetaConversionsApiSchema,
  StartFlowActionSchema,
  TriggerZapActionSchema,
  UnsetCUFActionSchema,
  SubscribeSequenceActionSchema,
  UnsubscribeSequenceActionSchema,
  CustomAudienceUserActionSchema,
  CustomAudienceIGUserActionSchema,
  HubSpotActionDataSchema,
  ActiveCampaignActionDataSchema,
  MailchimpActionDataSchema,
  ConvertKitActionDataSchema,
  ChatGPTActionDataSchema,
  ClaudeActionDataSchema,
  DeepSeekActionDataSchema,
  GoogleSheetsActionDataSchema,
  KlaviyoActionDataSchema,
  ...EmptyActionSchemas,
])

export type Action = z.infer<typeof ActionSchema>
/* Optional: individual variant types */
export type AddTagAction = z.infer<typeof AddTagActionSchema>
export type RemoveTagAction = z.infer<typeof RemoveTagActionSchema>
export type AssignConversationAction = z.infer<typeof AssignConversationActionSchema>
export type AppsRequestAction = z.infer<typeof AppsRequestActionSchema>
export type ExternalRequestAction = z.infer<typeof ExternalRequestActionSchema>
export type FireCustomEventAction = z.infer<typeof FireCustomEventActionSchema>
export type NotifyAdminsAction = z.infer<typeof NotifyAdminsActionSchema>
export type PauseAutomationsAction = z.infer<typeof PauseAutomationsActionSchema>
export type SetCUFAction = z.infer<typeof SetCUFActionSchema>
export type SetGAFAction = z.infer<typeof SetGAFActionSchema>
export type SetMainMenuAction = z.infer<typeof SetMainMenuActionSchema>
export type StartFlowAction = z.infer<typeof StartFlowActionSchema>
export type TriggerZapAction = z.infer<typeof TriggerZapActionSchema>
export type UnsetCUFAction = z.infer<typeof UnsetCUFActionSchema>
export type SubscribeSequenceAction = z.infer<typeof SubscribeSequenceActionSchema>
export type UnsubscribeSequenceAction = z.infer<typeof UnsubscribeSequenceActionSchema>
export type CustomAudienceUserAction = z.infer<typeof CustomAudienceUserActionSchema>
export type CustomAudienceIGUserAction = z.infer<typeof CustomAudienceIGUserActionSchema>

export type ApplicationData = z.infer<typeof ApplicationDataSchema>
export type ActionField = z.infer<typeof ActionFieldSchema>
export type ActionDataMapping = z.infer<typeof ActionDataMappingSchema>
export type ActionFieldValue = z.infer<typeof ActionFieldValueSchema>
